/**
 * MarketingSeo — runtime injector for marketing-page head metadata + JSON-LD.
 *
 * Replaces the per-app `<DocumentHead>` + `<SchemaMarkup>` duplicates.
 * Manages the page <title>, meta description, og:*, twitter:*, canonical,
 * hreflang alternates, and one `<script type="application/ld+json">` per
 * configured schema template.
 *
 * Patterns:
 *   - All updates write to the live `document.head`. We track every
 *     element we created so the cleanup pass on unmount removes only
 *     what THIS component owns.
 *   - hreflang alternates are emitted as `<link rel="alternate"
 *     hreflang="<code>" href="<url>">`. The component derives the URL
 *     for each locale from the configured `localeUrlBuilder`.
 *
 * Production-time sitemap.xml + robots.txt emission is handled separately
 * by the Vite build plugin shipped alongside this component.
 *
 * AND IT PUBLISHES ITS JSON-LD TO THE PRERENDER (LINKABLE-ASSETS-001 X1). The JSON-LD blobs join
 * the head-collector envelope at render time beside the scalars, so a build-time prerender bakes a
 * route's own structured data into its document for the crawlers that run no JavaScript. On
 * hydration the effect ADOPTS the prerendered `<script type="application/ld+json">` blocks (the
 * ones flagged with `@puffin-core/seo`'s `SEO_MANAGED_HEAD_ATTR`) in order, rather than appending a second copy of each.
 * The canonical and the hreflang alternates are the page's own, as authored: a marketing route is
 * ONE address (owner ruling Q-MES-URL), so nothing here derives a sibling address from the language
 * in force.
 *
 * @module @puffin-core/marketing-shell/MarketingSeo
 */

import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { SEO_MANAGED_HEAD_ATTR } from '@puffin-core/seo/managed-head';
import { collectHead } from '../head/headCollector.js';
import { useMarketingPageLanguage } from '../i18n/MarketingLanguage.jsx';

const JSON_LD_TYPE = 'application/ld+json';

/**
 * @typedef {Object} HreflangAlternate
 * @property {string} hreflang
 * @property {string} href
 */

/**
 * @typedef {Object} MarketingSeoProps
 * @property {string} title                                     <title>.
 * @property {string} [description]                             meta description.
 * @property {string} [canonical]                               <link rel="canonical">.
 * @property {string} [ogType='website']
 * @property {string} [ogImage]                                 absolute URL or path.
 * @property {string} [twitterCard='summary_large_image']
 * @property {string} [twitterSite]                             the site's X handle, its at-sign included.
 *                                                              (Written out: a bare at-sign word here reads
 *                                                              as a new tag and ends this typedef.)
 * @property {readonly HreflangAlternate[]} [hreflangAlternates]
 * @property {readonly Record<string, unknown>[]} [jsonLd]      JSON-LD blobs to emit
 *                                                              (typically built via schemaTemplates).
 * @property {string} [inLanguage]                              The page CONTENT's language, when it is
 *                                                              fixed whatever the chrome shows (a
 *                                                              legal document); absent = the
 *                                                              marketing site's language in force,
 *                                                              or none outside one. Names the WebPage
 *                                                              JSON-LD node's language only.
 * @property {string} [robots]                                  <meta name="robots"> directive
 *                                                              (e.g. 'noindex, nofollow' for a page
 *                                                              that must never be indexed). Absent =
 *                                                              no robots meta: the page is indexable.
 */

/**
 * `title` is REQUIRED: it becomes `document.title`, `og:title` and `twitter:title` unconditionally.
 * Every other prop is written only when given.
 *
 * @param {MarketingSeoProps} props
 * @returns {null} it renders nothing; it writes the document head
 */
export default function MarketingSeo({
  title,
  description,
  canonical,
  ogType = 'website',
  ogImage,
  twitterCard = 'summary_large_image',
  twitterSite,
  hreflangAlternates,
  jsonLd,
  inLanguage,
  robots,
}) {
  /*
   * SSR (MSSG-001). Everything below happens in a `useEffect` against a real `document` — which
   * is right in a browser and inert during a build-time prerender, where effects never run and
   * no document exists. That meant every prerendered route inherited the SHELL's <title> and
   * description: /features and /pricing each announcing themselves as the homepage to exactly
   * the crawlers (GPTBot, ClaudeBot, PerplexityBot) that read nothing else.
   *
   * Publishing to the head collector during RENDER is what survives SSR. It is a no-op in a
   * browser — no collector is ever active there — so nothing about client behaviour changes.
   */
  // The WebPage node names the language the page shows: the content language its caller declares,
  // else — on a marketing site, whose pages are translated with the chrome — the language in force.
  // Anywhere else an undeclared page names none and the node is left as built (F-Q7-2).
  const marketingLanguage = useMarketingPageLanguage();
  const pageLanguage = inLanguage || marketingLanguage;

  /*
   * CONTENT-KEY-001. `hreflangAlternates` / `jsonLd` are array props that callers
   * rebuild inline on every render, so depending on the arrays THEMSELVES would
   * tear down and re-append every managed <link>/<script> on every render. The
   * effect therefore depends on CONTENT, not identity.
   *
   * The previous form put `JSON.stringify(hreflangAlternates || [])` directly in
   * the dependency array while the effect body read the raw arrays. That is
   * invisible to `react-hooks/exhaustive-deps` — it (correctly) reported the raw
   * arrays as missing dependencies AND the inline stringify as an unverifiable
   * complex expression. Round-tripping through the serialized form instead means
   * these memo bodies read ONLY their key, and the effect reads ONLY these
   * values: every dependency array below is literal and machine-checked, with
   * the same "re-run only when content changes" behaviour.
   *
   * The round-trip is lossless for this data by contract: both props are
   * JSON-serializable (the JSON-LD blobs are themselves emitted via
   * `JSON.stringify` into a <script type="application/ld+json">).
   */
  const hreflangAlternatesKey = JSON.stringify(hreflangAlternates || []);
  const jsonLdKey = JSON.stringify(
    Array.isArray(jsonLd)
      ? jsonLd.map((blob) =>
          pageLanguage && blob['@type'] === 'WebPage'
            ? { ...blob, inLanguage: pageLanguage }
            : blob,
        )
      : jsonLd || [],
  );
  const stableHreflangAlternates = useMemo(
    () => JSON.parse(hreflangAlternatesKey),
    [hreflangAlternatesKey],
  );
  const stableJsonLd = useMemo(() => JSON.parse(jsonLdKey), [jsonLdKey]);

  // X1 — what the prerender bakes into the route's document: the scalars and, when the page has
  // any, its JSON-LD, exactly the blocks the effect below emits (a non-object blob is skipped there,
  // so it is skipped here). `collectHead` keeps only the keys that carry a value.
  const publishedJsonLd = Array.isArray(stableJsonLd)
    ? stableJsonLd.filter((blob) => blob && typeof blob === 'object')
    : [];
  collectHead({
    title,
    description,
    canonical,
    ogType,
    ogImage,
    inLanguage: pageLanguage,
    jsonLd: publishedJsonLd.length > 0 ? publishedJsonLd : undefined,
  });

  useEffect(() => {
    const owned = [];
    const restoreOps = [];

    // <title> is per-PAGE and is restored on unmount, so we don't leave the
    // host page announcing a route it has navigated away from.
    const originalTitle = document.title;
    document.title = title;
    restoreOps.push(() => {
      document.title = originalTitle;
    });

    /*
     * `<html lang>` is NOT written here (LIFE-SHORTLISTED-001 F-Q7-2, WCAG 3.1.1). Its one writer is
     * `useSyncHtmlLang` (`@puffin-core/i18n-bootstrap`), which `createMarketingApp` mounts (through
     * `<MarketingLanguage>`) and which follows the language in force. This component used to write
     * each page's literal `inLanguage="en"`, so a page rendering Spanish announced English to a
     * screen reader.
     */
    if (description) {
      owned.push(upsertMeta({ name: 'description', content: description }));
    }
    // LIFE-SHORTLISTED-001 P6.2b — a CMS preview link renders unpublished edits inside the tenant's
    // indexable site shell, so the PAGE must say `noindex` itself. Client-side only: no prerendered
    // surface carries a robots directive, so it is not published to the head collector.
    if (robots) {
      owned.push(upsertMeta({ name: 'robots', content: robots }));
    }
    if (canonical) {
      owned.push(upsertLink({ rel: 'canonical', href: canonical }));
    }
    owned.push(upsertMeta({ property: 'og:title', content: title }));
    owned.push(upsertMeta({ property: 'og:type', content: ogType }));
    if (description) {
      owned.push(upsertMeta({ property: 'og:description', content: description }));
    }
    if (canonical) {
      owned.push(upsertMeta({ property: 'og:url', content: canonical }));
    }
    if (ogImage) {
      owned.push(upsertMeta({ property: 'og:image', content: ogImage }));
    }
    owned.push(upsertMeta({ name: 'twitter:card', content: twitterCard }));
    owned.push(upsertMeta({ name: 'twitter:title', content: title }));
    if (description) {
      owned.push(upsertMeta({ name: 'twitter:description', content: description }));
    }
    if (ogImage) {
      owned.push(upsertMeta({ name: 'twitter:image', content: ogImage }));
    }
    if (twitterSite) {
      owned.push(upsertMeta({ name: 'twitter:site', content: twitterSite }));
    }

    if (Array.isArray(stableHreflangAlternates)) {
      for (const alt of stableHreflangAlternates) {
        owned.push(upsertLink({ rel: 'alternate', hreflang: alt.hreflang, href: alt.href }));
      }
    }

    if (Array.isArray(stableJsonLd)) {
      // X1 — a prerendered document already carries this page's blocks, flagged as managed. Adopt
      // them in order (the prerender wrote them in this order) instead of appending a second copy
      // of each; whatever the server wrote beyond this render's blobs is this component's too.
      const prerendered = [
        ...document.head.querySelectorAll(
          `script[type="${JSON_LD_TYPE}"][${SEO_MANAGED_HEAD_ATTR}]`,
        ),
      ];
      let adopted = 0;
      for (const blob of stableJsonLd) {
        if (!blob || typeof blob !== 'object') continue;
        let script = prerendered[adopted];
        if (script) {
          adopted += 1;
        } else {
          script = document.createElement('script');
          script.type = JSON_LD_TYPE;
          script.setAttribute(SEO_MANAGED_HEAD_ATTR, '1');
          document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(blob);
        owned.push(script);
      }
      for (const surplus of prerendered.slice(adopted)) {
        surplus.remove();
      }
    }

    return () => {
      for (const el of owned) {
        if (el && el.parentNode) {
          el.parentNode.removeChild(el);
        }
      }
      // Run restores in reverse so multiple restores compose correctly.
      for (let i = restoreOps.length - 1; i >= 0; i -= 1) {
        restoreOps[i]();
      }
    };
  }, [
    title,
    description,
    canonical,
    ogType,
    ogImage,
    twitterCard,
    twitterSite,
    inLanguage,
    robots,
    stableHreflangAlternates,
    stableJsonLd,
  ]);

  return null;
}

function upsertMeta({ name, property, content }) {
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    if (name) el.setAttribute('name', name);
    if (property) el.setAttribute('property', property);
    el.setAttribute(SEO_MANAGED_HEAD_ATTR, '1');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return el.getAttribute(SEO_MANAGED_HEAD_ATTR) ? el : null;
}

function upsertLink({ rel, href, hreflang }) {
  // hreflang alternates can co-exist with the same `rel="alternate"` so
  // we narrow the selector by hreflang when present. Canonical uses just
  // rel="canonical".
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    el.setAttribute(SEO_MANAGED_HEAD_ATTR, '1');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el.getAttribute(SEO_MANAGED_HEAD_ATTR) ? el : null;
}

MarketingSeo.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  canonical: PropTypes.string,
  ogType: PropTypes.string,
  ogImage: PropTypes.string,
  twitterCard: PropTypes.string,
  twitterSite: PropTypes.string,
  hreflangAlternates: PropTypes.arrayOf(
    PropTypes.shape({
      hreflang: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
    }),
  ),
  jsonLd: PropTypes.arrayOf(PropTypes.object),
  inLanguage: PropTypes.string,
  robots: PropTypes.string,
};
